import { type FormEvent, useEffect, useRef, useState } from "react";
import type { AgentTheme, Citation } from "@bubble/core";
import { api, messageOf, streamApi } from "../lib/api.ts";
import { useToken } from "../lib/auth.tsx";

export function askPreview(agentId: string, text: string): void {
  window.dispatchEvent(new CustomEvent("bubble-ask", { detail: { agentId, text } }));
}

type Turn = { role: "user" | "assistant"; text: string; citations: Citation[] };

export function ChatBubble({
  agentId,
  name,
  theme,
  note,
  placement,
  defaultOpen = false,
  onAnswered,
}: {
  agentId: string;
  name: string;
  theme: AgentTheme;
  note: string;
  placement: "fixed" | "stage";
  defaultOpen?: boolean;
  onAnswered?: () => void;
}) {
  const token = useToken();
  const title = theme.title?.trim() || name;
  const [open, setOpen] = useState(defaultOpen);
  const [hint, setHint] = useState(!defaultOpen);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [message, setMessage] = useState("");
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [collect, setCollect] = useState(false);
  const [contact, setContact] = useState({ name: "", email: "", note: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const logRef = useRef<HTMLDivElement>(null);
  const busyRef = useRef(false);
  const sendRef = useRef<(text: string) => Promise<void>>(async () => undefined);

  async function send(text: string): Promise<void> {
    const question = text.trim();
    if (!question || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError("");
    setOpen(true);
    setHint(false);
    setTurns((current) => [...current, { role: "user", text: question, citations: [] }, { role: "assistant", text: "", citations: [] }]);
    try {
      await streamApi("/v1/chat/preview", token, { agentId, message: question, conversationId }, (eventName, data) => {
        if (eventName === "token" && typeof data.text === "string") {
          const delta = data.text;
          setTurns((current) => updateLast(current, (turn) => ({ ...turn, text: turn.text + delta })));
        }
        if (eventName === "citations" && Array.isArray(data.citations)) {
          const citations = data.citations.filter(isCitation);
          setTurns((current) => updateLast(current, (turn) => ({ ...turn, citations })));
        }
        if (eventName === "fallback" && data.behavior === "collect_contact") setCollect(true);
        if (eventName === "done" && typeof data.conversationId === "string") {
          setConversationId(data.conversationId);
          onAnswered?.();
        }
        if (eventName === "error" && typeof data.message === "string") setError(data.message);
      });
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  sendRef.current = send;

  useEffect(() => {
    function onAsk(event: Event): void {
      const detail = (event as CustomEvent<{ agentId?: string; text?: string }>).detail;
      if (detail?.agentId !== agentId || typeof detail.text !== "string") return;
      void sendRef.current(detail.text);
    }
    window.addEventListener("bubble-ask", onAsk);
    return () => window.removeEventListener("bubble-ask", onAsk);
  }, [agentId]);

  useEffect(() => {
    const log = logRef.current;
    if (log) log.scrollTop = log.scrollHeight;
  }, [turns, collect, open]);

  async function onAsk(event: FormEvent) {
    event.preventDefault();
    const question = message;
    setMessage("");
    await send(question);
  }

  async function onContact(event: FormEvent) {
    event.preventDefault();
    setError("");
    try {
      await api("/v1/chat/preview/contact", token, {
        method: "POST",
        json: { agentId, conversationId, name: contact.name, email: contact.email, message: contact.note },
      });
      setCollect(false);
      setTurns((current) => [...current, { role: "assistant", text: "Thanks. We have your note.", citations: [] }]);
    } catch (caught) {
      setError(messageOf(caught));
    }
  }

  const color = /^#[0-9a-fA-F]{6}$/.test(theme.color) ? theme.color : "#ef5b25";
  const ink = inkFor(color);

  return (
    <div className={`bubble-dock ${placement}`}>
      {open ? (
        <section className="bubble-panel" aria-label={`Chat with ${title}`}>
          <header>
            {theme.avatarUrl ? <img className="bubble-avatar" src={theme.avatarUrl} alt="" /> : <span className="bubble-avatar" style={{ background: color }}>{title.slice(0, 1).toUpperCase()}</span>}
            <div>
              <strong>{title}</strong>
              <p>Answers from this business's files</p>
            </div>
            <button type="button" className="bubble-close" onClick={() => setOpen(false)}>
              Close
            </button>
          </header>
          <div className="bubble-log" ref={logRef} aria-live="polite">
            <article className="assistant">
              <p>{theme.greeting}</p>
            </article>
            {turns.map((turn, index) => (
              <article key={`${turn.role}-${index}`} className={turn.role} style={turn.role === "user" ? { background: color, color: ink } : undefined}>
                <p>{turn.text || (busy && index === turns.length - 1 ? "…" : "")}</p>
                {turn.citations.length > 0 ? (
                  <ul>
                    {turn.citations.map((citation) => (
                      <li key={citation.chunkId}>
                        <strong>{citation.label}</strong> {citation.excerpt}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </article>
            ))}
          </div>
          {collect ? (
            <form className="bubble-contact" onSubmit={onContact}>
              <label>
                Name
                <input value={contact.name} onChange={(event) => setContact({ ...contact, name: event.target.value })} />
              </label>
              <label>
                Email
                <input required type="email" value={contact.email} onChange={(event) => setContact({ ...contact, email: event.target.value })} />
              </label>
              <label>
                Note
                <textarea rows={2} value={contact.note} onChange={(event) => setContact({ ...contact, note: event.target.value })} />
              </label>
              <button type="submit">Send</button>
            </form>
          ) : null}
          {error ? <p className="bubble-error">{error}</p> : null}
          <p className="bubble-note">{note}</p>
          <form className="bubble-composer" onSubmit={onAsk}>
            <textarea
              rows={2}
              required
              value={message}
              placeholder="Ask a question"
              onChange={(event) => setMessage(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  event.currentTarget.form?.requestSubmit();
                }
              }}
            />
            <button type="submit" disabled={busy} style={{ background: color, color: ink }}>
              {busy ? "Answering…" : "Send"}
            </button>
          </form>
        </section>
      ) : null}
      {hint && !open ? <span className="bubble-hint">Try the chat</span> : null}
      <button
        type="button"
        className="bubble-launcher"
        style={{ background: color, color: ink }}
        aria-expanded={open}
        aria-label={open ? `Close chat with ${title}` : `Open chat with ${title}`}
        onClick={() => {
          setOpen((value) => !value);
          setHint(false);
        }}
      >
        <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true">
          <path fill="currentColor" d="M5 6.5A3.5 3.5 0 0 1 8.5 3h7A3.5 3.5 0 0 1 19 6.5v6A3.5 3.5 0 0 1 15.5 16H11l-3.6 3.2c-.7.6-1.8.1-1.8-.8V16A3.5 3.5 0 0 1 5 12.5v-6Z" />
        </svg>
      </button>
    </div>
  );
}

function updateLast(turns: Turn[], map: (turn: Turn) => Turn): Turn[] {
  const next = [...turns];
  const last = next[next.length - 1];
  if (last) next[next.length - 1] = map(last);
  return next;
}

function isCitation(value: unknown): value is Citation {
  if (!value || typeof value !== "object") return false;
  const record = value as Partial<Citation>;
  return typeof record.chunkId === "string" && typeof record.label === "string" && typeof record.excerpt === "string";
}

function inkFor(hex: string): string {
  const red = Number.parseInt(hex.slice(1, 3), 16);
  const green = Number.parseInt(hex.slice(3, 5), 16);
  const blue = Number.parseInt(hex.slice(5, 7), 16);
  return (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255 > 0.72 ? "#1b1914" : "#fffdf8";
}
